import { useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Computer from "@mui/icons-material/Computer";
import Edit from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { CellText } from "components/Omnisearch/helpComponents/CellText";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  SignatureType,
  SubmissionStatus,
  SubmissionVirtualEventsQuery,
  UserType,
} from "generated-graphql/graphql";
import { useAutoFlex } from "hooks/useAutoFlex";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  OmnisearchGridColDef,
  useOmnisearchDatagrid,
} from "hooks/useOmnisearchDatagrid";
import { DateTime } from "luxon";
import { SUBMISSION_VIRTUAL_EVENTS_QUERY } from "queries/submissions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SubmissionsEmptyState } from "./SubmissionsEmptyState";
import { SubmissionBulkActionsMenu } from "./SubmissionBulkActionsMenu";

export type Row =
  SubmissionVirtualEventsQuery["submissionVirtualEvents"]["items"][0];

export const STATUS_LABELS: Record<string, string> = {
  [SubmissionStatus.NotStarted]: "🔶 Upcoming",
  [SubmissionStatus.InProgress]: "🚧 In Progress",
  [SubmissionStatus.EncampInitialReview]: "⛺️ In Encamp Review",
  [SubmissionStatus.InReview]: "🔍 In Review",
  [SubmissionStatus.ProcessingSubmission]: "✅ Awaiting Submission",
  [SubmissionStatus.FilingComplete]: "🎉 Submitted",
  [SubmissionStatus.NotReporting]: "⭕️ Not Reporting",
};

const TIME_FILTER_OPTIONS = {
  upcoming: "🔶 Upcoming",
  "in-progress": "💬 Current",
  completed: "🎉 Completed",
};

const getStatusLabel = (status: string): string => {
  return STATUS_LABELS[status] || status;
};

const TIME_FILTER_TITLES: Record<string, string> = {
  upcoming: "Upcoming Submissions",
  "in-progress": "Current Submissions",
  completed: "Completed Submissions",
};

const getTimeFilterTitle = (timeFilter: string | null): string | undefined => {
  if (!timeFilter) return undefined;
  return TIME_FILTER_TITLES[timeFilter];
};

export function WasteSubmissions() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { isStaff, user } = useCurrentUser();
  const [selectedRows, setSelectedRows] = useState<Row[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleRowClick = useCallback(
    (row: Row) => {
      const submissionId = row.submission?.id;
      if (!isStaff) {
        navigate(`/o/${tenantId}/waste/submissions/${submissionId}`);
        return;
      }

      // Staff users: check submission status
      const isVirtualSubmission =
        row.submission?.status === SubmissionStatus.NotStarted;

      if (isVirtualSubmission) {
        // Navigate to create submission with taskId and startDate parameters
        const dateOnly = DateTime.fromISO(String(row.deadline)).toISODate();
        navigate(
          `/o/${tenantId}/waste/submissions/create?taskId=${row.taskId}&startDate=${dateOnly}`
        );
      } else {
        // Check if staff user is a reviewer for submissions in review status
        const isInReview =
          row.submission?.status === SubmissionStatus.EncampInitialReview;
        const isStaffReviewer =
          isInReview &&
          row.submission?.reviewers?.some(
            (reviewer) => reviewer.user.id === user?.id
          );

        if (isStaffReviewer) {
          // Staff reviewers should go to read-only review mode
          navigate(`/o/${tenantId}/waste/submissions/${submissionId}`);
        } else {
          // All other submissions go directly to edit mode
          navigate(`/o/${tenantId}/waste/submissions/${submissionId}/edit`);
        }
      }
    },
    [isStaff, navigate, tenantId, user?.id]
  );

  const handleAddSubmission = useCallback(() => {
    navigate(`/o/${tenantId}/waste/submissions/create`);
  }, [navigate, tenantId]);

  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Submissions" },
  ]);

  const omnisearchPersistenceKey = "submissions-list-view-v9";
  const autoFlex = useAutoFlex(
    1.0,
    {
      deadline: 0.1,
      title: 0.25,
      facilities: 0.1,
      "submission.status": 0.1,
      submissionType: 0.1,
    },
    [
      "facilities",
      "submissionType",
      "title",
      "documents",
      "reviewers",
      "signer",
      "staffAssignee",
      "submission.status",
    ]
  );

  const columns: OmnisearchGridColDef<Row>[] = [
    {
      field: "deadline",
      headerName: "Due Date",
      sortable: true,
      flex: autoFlex.get("deadline"),
      minWidth: 20,
      renderCell: ({ value }) => {
        return (
          <CellText variant="body2">
            {DateTime.fromISO(value).toFormat("MM/dd/yyyy")}
          </CellText>
        );
      },
    },
    {
      field: "facilities",
      headerName: "Facilities",
      flex: autoFlex.get("facilities"),
      minWidth: 20,
      sortable: true,
      renderCell({ row }) {
        return (
          <Stack
            spacing={0}
            style={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              height: "100%",
              width: "100%",
              alignItems: "start",
              overflow: "hidden",
            }}
          >
            {(row.facilities ?? []).map((facility) => (
              <Typography
                key={facility.id}
                variant="body2"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                <Tooltip title={facility.name}>
                  <span>{facility.name}</span>
                </Tooltip>
              </Typography>
            ))}
          </Stack>
        );
      },
    },
    {
      field: "title",
      headerName: "Title",
      flex: autoFlex.get("title"),
      minWidth: 100,
      sortable: true,
      renderCell: ({ row: { title } }) => {
        return (
          <CellText variant="body2" noWrap>
            {title}
          </CellText>
        );
      },
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: autoFlex.get("notes"),
      minWidth: 100,
      sortable: false,
      renderCell: ({ row }) => {
        const notes = row.submission?.notes;
        if (!notes) return "";
        return (
          <CellText variant="body2" noWrap>
            {notes}
          </CellText>
        );
      },
    },
    {
      field: "documents",
      headerName: "Attachment(s)",
      flex: autoFlex.get("documents"),
      minWidth: 60,
      sortable: false,
      renderCell: (params) => {
        const documents = params.row.submission?.documents || [];

        if (documents.length === 0) return "";
        const mappedDocuments = documents
          .map((doc) => doc.document.title)
          .join(", ");
        return (
          <Tooltip title={mappedDocuments}>
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {mappedDocuments}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "reviewers",
      headerName: "Reviewer(s)",
      flex: autoFlex.get("reviewers"),
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const reviewers = params.row.submission?.reviewers || [];
        const reviewerNames = reviewers
          .filter(
            (r) => !r.isSigner && (isStaff || r.user.type === UserType.Customer)
          )
          .map(
            (r) =>
              `${r.user.person?.first || ""} ${
                r.user.person?.last || ""
              }`.trim() || r.user.email
          );

        if (reviewerNames.length === 0) return "";

        return (
          <Tooltip title={reviewerNames.join(", ")}>
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {reviewerNames.join(", ")}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "signer",
      headerName: "Signer",
      flex: autoFlex.get("signer"),
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const reviewers = params.row.submission?.reviewers || [];
        const signers = reviewers.filter((r) => r.isSigner);
        const signatureType = params.row.submission?.signatureType;

        // Handle no signature required cases
        if (signatureType === SignatureType.None || signers.length === 0) {
          return (
            <CellText variant="body2" noWrap>
              No Signature Required
            </CellText>
          );
        }

        const signerNames = signers.map(
          (r) =>
            `${r.user.person?.first || ""} ${
              r.user.person?.last || ""
            }`.trim() || r.user.email
        );

        // Determine icon and label based on signature type
        let icon = null;
        let label = "";
        if (signatureType === SignatureType.Digital) {
          icon = <Computer sx={{ fontSize: 16, mr: 0.5 }} />;
          label = "Digital";
        } else if (signatureType === SignatureType.WetInk) {
          icon = <Edit sx={{ fontSize: 16, mr: 0.5 }} />;
          label = "Wet Ink";
        } else {
          // Default to wet ink if signatureType is not set but signers exist
          icon = <Edit sx={{ fontSize: 16, mr: 0.5 }} />;
          label = "Wet Ink";
        }

        return (
          <Tooltip title={`${label}: ${signerNames.join(", ")}`}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {icon}
              <Typography variant="body2" component="span">
                {label}: {signerNames.join(", ")}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "staffAssignee",
      headerName: "Encamp Assignee",
      flex: autoFlex.get("staffAssignee"),
      minWidth: 90,
      valueGetter: (_, row) => {
        if (!row?.submission?.staffAssignee) return "";
        const person = row.submission.staffAssignee.person;
        if (!person) return row.submission.staffAssignee.email;
        return (
          `${person.first || ""} ${person.last || ""}`.trim() ||
          row.submission.staffAssignee.email
        );
      },
    },
    {
      field: "submission.status",
      headerName: "Status",
      flex: autoFlex.get("submission.status"),
      minWidth: 120,
      sortable: true,
      renderCell({ row: { submission } }) {
        return (
          <CellText variant="body2" noWrap>
            {getStatusLabel(submission?.status ?? SubmissionStatus.NotStarted)}
          </CellText>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      sortable: false,
      align: "right",
      getActions: ({ row }) => {
        const actions: any[] = [];

        if (row.submission?.status === SubmissionStatus.NotStarted) {
          actions.push(
            <Tooltip title="Create Submission" key="createSubmission">
              <GridActionsCellItem
                onClick={() => handleRowClick(row)}
                label="Create Submission"
                icon={<Add />}
              />
            </Tooltip>
          );
        }

        return actions;
      },
    },
  ];

  const {
    omnisearch,
    setOmnisearch,
    getOmnisearchValue,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
    columns: omnisearchColumns,
    sortModel,
    pageSize,
    page,
  } = useOmnisearchDatagrid({
    persistenceKey: omnisearchPersistenceKey,
    isURLDriven: true,
    columns,
    initialSortModel: [
      { field: "dueDate", sort: "asc" },
      { field: "title", sort: "asc" },
    ],
    initialPageSize: 50,
  });

  const handleAssignedToMeChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        editOmnisearchKeyValues([["assignedToMe", "true"]]);
      } else {
        removeOmnisearchKeyValue("assignedToMe");
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  const handleTimeFilterChange = useCallback(
    (newTimeFilter: string | null) => {
      if (newTimeFilter && newTimeFilter !== "") {
        editOmnisearchKeyValues([["timeFilter", newTimeFilter]]);
      } else {
        removeOmnisearchKeyValue("timeFilter");
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  const handleViewUpcoming = useCallback(() => {
    editOmnisearchKeyValues([["timeFilter", "upcoming"]]);
  }, [editOmnisearchKeyValues]);

  const clearOmnisearch = useCallback(() => {
    setOmnisearch("");
  }, [setOmnisearch]);

  const timeFilter = getOmnisearchValue("timeFilter") || "";
  const assignedToMe = getOmnisearchValue("assignedToMe") === "true";

  // Use the same query as the grid to get loading/data state
  const search = useMemo(
    () =>
      [`tenantId:${tenantId} status:ACTIVE|OVERDUE`, omnisearch]
        .join(" ")
        .trim(),
    [tenantId, omnisearch]
  );

  // Clear bulk selections when filtering changes
  useEffect(() => {
    setSelectedRows([]);
    setSelectedIds([]);
  }, [omnisearch]);

  const { data, loading } = useQuery(SUBMISSION_VIRTUAL_EVENTS_QUERY, {
    variables: {
      search,
      page,
      pageSize,
      sort: [...sortModel],
    },
    fetchPolicy: "cache-and-network",
  });

  const hasData = useMemo(() => {
    if (!data) return null;
    const count = data.submissionVirtualEvents?.count ?? 0;
    return count > 0;
  }, [data]);

  const titleFilter = useMemo(() => {
    const timeFilterTitle = getTimeFilterTitle(timeFilter);
    if (timeFilterTitle) {
      return timeFilterTitle;
    } else {
      return assignedToMe ? "My Submissions" : "All Submissions";
    }
  }, [timeFilter, assignedToMe]);

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        name: "Submission Info",
        fields: ["submission.title", "submissionType", "dueDate", "documents"],
      },
      {
        name: "Location",
        fields: ["facilityId"],
      },
      {
        name: "Assignment",
        fields: ["reviewers", "signer", "staffAssignee"],
      },
    ],
    []
  );

  return (
    <Stack sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack
        id="submission-filter-header"
        paddingX={3}
        paddingY={2}
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderBottomColor: "divider",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Stack direction="row" gap={2} alignItems="flex-start">
            <FormControl variant="standard" size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="time-filter-label" shrink>
                Status
              </InputLabel>
              <Select
                labelId="time-filter-label"
                value={timeFilter}
                onChange={(e) => handleTimeFilterChange(e.target.value)}
                label="Status"
                variant="standard"
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return "📄 All";
                  }
                  return TIME_FILTER_OPTIONS[
                    selected as keyof typeof TIME_FILTER_OPTIONS
                  ];
                }}
              >
                <MenuItem value="">📄 All</MenuItem>
                {Object.entries(TIME_FILTER_OPTIONS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={assignedToMe}
                  onChange={(e) => handleAssignedToMeChange(e.target.checked)}
                  color="primary"
                />
              }
              label="Assigned to me"
            />
          </Stack>
          {isStaff && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddSubmission}
              color="secondary"
            >
              Create Submission
            </Button>
          )}
        </Stack>
      </Stack>
      <Stack
        id="submission-list"
        p={2}
        sx={{
          background: theme.palette.grey50.main,
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            pb: 3,
          }}
        >
          <Box sx={{ p: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={1}
            >
              <Box>
                <Typography
                  variant="h5"
                  component="h5"
                  color="text.primary"
                  pb={1}
                >
                  {titleFilter}
                </Typography>
                <Typography fontSize={12} component="p">
                  General waste reporting submissions. Use the search and
                  filters to find specific submissions.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {!hasData && !loading && (
            <SubmissionsEmptyState
              timeFilter={timeFilter}
              assignedToMe={assignedToMe}
              isStaff={isStaff}
              onCreateSubmission={handleAddSubmission}
              onViewUpcoming={handleViewUpcoming}
              omnisearch={omnisearch}
              onClearSearch={clearOmnisearch}
            />
          )}

          <Box
            /**
             * This needs to be mounted in the DOM even when there is no data to display.
             *
             * This is because the the `OmnisearchDataGrid` component sets the `initialFilters`
             * everytime it gets mounted.
             *
             * This causes finicky behavior when updating the the status dropdown where you select
             * an option and it immediately switches away from your selection to the `initialFilters`
             */
            sx={{
              display: hasData ? "block" : "none",
            }}
          >
            <OmnisearchDataGrid
              persistenceKey={omnisearchPersistenceKey}
              isURLDriven
              initialFilters={[{ field: "timeFilter", value: "in-progress" }]}
              columns={omnisearchColumns}
              dataQuery={SUBMISSION_VIRTUAL_EVENTS_QUERY}
              defaultSearch={`tenantId:${tenantId} status:ACTIVE|OVERDUE`}
              getItems={(data) => data.submissionVirtualEvents.items}
              getCount={(data) => data.submissionVirtualEvents.count ?? 0}
              checkboxSelection={isStaff}
              selectedIds={isStaff ? selectedIds : undefined}
              onSelectedRowsChanged={
                isStaff ? (rows) => setSelectedRows(rows) : undefined
              }
              onSelectedIdsChanged={
                isStaff ? (ids) => setSelectedIds(ids as string[]) : undefined
              }
              onRowClick={({ row }) => handleRowClick(row)}
              commandButtons={
                isStaff
                  ? [
                      <SubmissionBulkActionsMenu
                        key="bulk-actions"
                        submissions={selectedRows}
                        onClearSelections={() => {
                          setSelectedRows([]);
                          setSelectedIds([]);
                        }}
                      />,
                    ]
                  : []
              }
              initialPageSize={25}
              rowHeight={"auto"}
              columnVisibilityModel={{
                notes: false,
                ...(isMobile && {
                  facilities: false,
                  tags: false,
                }),
              }}
              additionalFilterColumns={[
                {
                  header: "Facility",
                  key: "facilityId",
                  filterKeyType: "multipleFacility",
                },
                {
                  header: "Attachment Name",
                  key: "documents",
                  filterKeyType: "text",
                },
                {
                  header: "Submission Type",
                  key: "submissionType",
                  filterKeyType: "text",
                },
                {
                  header: "Due Date",
                  key: "dueDate",
                  filterKeyType: "date",
                  dateRangeShortcuts: true,
                },
                {
                  header: "Reviewer",
                  key: "reviewers",
                  filterKeyType: "text",
                },
                {
                  header: "Signer",
                  key: "signer",
                  filterKeyType: "text",
                },
                {
                  header: "Encamp Assignee",
                  key: "staffAssignee",
                  filterKeyType: "text",
                },
              ]}
              excludeFilterColumns={[
                "submissionType",
                "dueDate",
                "status",
                "documents",
                "reviewers",
                "signer",
                "staffAssignee",
                "facilityName",
                "title",
                "facilityId",
                "deadline",
                "facilities",
                "submission.status",
              ]}
              filterGroups={filterGroups}
              tenantId={tenantId}
            />
          </Box>
        </Paper>
      </Stack>
    </Stack>
  );
}
